import React, { useRef, useMemo, useState, useEffect, memo } from 'react';
import {
  ChartCanvas,
  Chart,
  CandlestickSeries,
  BarSeries,
  LineSeries,
  BollingerSeries,
  XAxis,
  YAxis,
  CrossHairCursor,
  MouseCoordinateX,
  MouseCoordinateY,
  EdgeIndicator,
  OHLCTooltip,
  MovingAverageTooltip,
  BollingerBandTooltip,
  GenericChartComponent,
  getAxisCanvas,
  discontinuousTimeScaleProviderBuilder,
  mouseBasedZoomAnchor,
  ema,
  sma,
  bollingerBand,
} from 'react-financial-charts';
import { timeFormat } from 'd3-time-format';
import { Settings2 } from 'lucide-react';
import { colors, chartMargin, priceFormat, volumeFormat } from '../charts/chartTheme';

const VISIBLE_CANDLES = 120;
const VOLUME_HEIGHT = 50;

/* ── Trade markers — filters by playbackIndex at draw time (no data rebuild) ── */
const TradeMarkersSeries = memo(function TradeMarkersSeries({ maxBarIndex }) {
  return (
    <GenericChartComponent
      canvasDraw={(ctx, moreProps) => {
        const { chartConfig: { yScale }, xScale, plotData, xAccessor } = moreProps;
        const limit = maxBarIndex ?? Infinity;
        ctx.save();
        for (const d of plotData) {
          if (!d.isTrade || d._backtestIndex > limit) continue;
          const x = Math.round(xScale(xAccessor(d)));
          const y = Math.round(yScale(d.tradePrice));
          const isBuy = d.tradeSide === 'buy';
          ctx.fillStyle = isBuy ? colors.tradeBuy : colors.tradeSell;
          ctx.beginPath();
          if (isBuy) {
            ctx.moveTo(x, y + 6);
            ctx.lineTo(x - 5, y + 14);
            ctx.lineTo(x + 5, y + 14);
          } else {
            ctx.moveTo(x, y - 6);
            ctx.lineTo(x - 5, y - 14);
            ctx.lineTo(x + 5, y - 14);
          }
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }}
      canvasToDraw={getAxisCanvas}
    />
  );
});

/* ── Grid level lines ─────────────────────────────────────────── */
const GridLevelLine = ({ yValue, stroke }) => (
  <GenericChartComponent
    canvasDraw={(ctx, moreProps) => {
      const { chartConfig: { yScale, width } } = moreProps;
      const y = Math.round(yScale(yValue));
      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 0.5;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
      ctx.restore();
    }}
    canvasToDraw={getAxisCanvas}
    drawOn={['pan']}
  />
);

/* ── Custom info tooltip — shows volume + trade details on hover ── */
const InfoTooltip = memo(function InfoTooltip() {
  return (
    <GenericChartComponent
      canvasDraw={(ctx, moreProps) => {
        const { currentItem, chartConfig: { width } } = moreProps;
        if (!currentItem) return;

        const lines = [];
        if (currentItem.volume != null) {
          lines.push({ label: 'Vol', value: volumeFormat(currentItem.volume), color: '#9ca3af' });
        }
        if (currentItem.isTrade) {
          const isBuy = currentItem.tradeSide === 'buy';
          lines.push({
            label: isBuy ? 'BUY' : 'SELL',
            value: `${currentItem.tradeQuantity} @ ${priceFormat(currentItem.tradePrice)}`,
            color: isBuy ? colors.tradeBuy : colors.tradeSell,
          });
        }
        if (lines.length === 0) return;

        ctx.save();
        ctx.font = '10px -apple-system, BlinkMacSystemFont, sans-serif';
        const x = 8;
        let y = 14;
        for (const line of lines) {
          ctx.fillStyle = '#6b7280';
          ctx.fillText(`${line.label}: `, x, y);
          const labelW = ctx.measureText(`${line.label}: `).width;
          ctx.fillStyle = line.color;
          ctx.fillText(line.value, x + labelW, y);
          y += 13;
        }
        ctx.restore();
      }}
      canvasToDraw={getAxisCanvas}
      drawOn={['mousemove', 'pan']}
    />
  );
});

/* ── Main chart component ─────────────────────────────────────── */
export default function BacktestChart({ bars, trades, gridLevels, playbackIndex, strategyType, overlays }) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [chartSettings, setChartSettings] = useState({
    zoom: true,
    pan: true,
    clamp: true,
    crosshair: true,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDims({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Indicator calculators (stable refs)
  const ema20Calc = useMemo(() => ema().id('ema20').options({ windowSize: 20 }).merge((d, c) => { d.ema20 = c; }).accessor(d => d.ema20), []);
  const ema50Calc = useMemo(() => ema().id('ema50').options({ windowSize: 50 }).merge((d, c) => { d.ema50 = c; }).accessor(d => d.ema50), []);
  const sma200Calc = useMemo(() => sma().id('sma200').options({ windowSize: 200 }).merge((d, c) => { d.sma200 = c; }).accessor(d => d.sma200), []);
  const bbCalc = useMemo(() => bollingerBand().merge((d, c) => { d.bb = c; }).accessor(d => d.bb), []);

  // Prepare chart data — only rebuilds when bars/trades change, NOT on playback tick
  const processedData = useMemo(() => {
    if (!bars || bars.length === 0) return [];

    let data = bars.map((b, i) => ({
      date: new Date(b.timestamp),
      open: b.open,
      high: b.high,
      low: b.low,
      close: b.close,
      volume: b.volume || 0,
      _backtestIndex: i,
    }));

    data = ema20Calc(ema50Calc(sma200Calc(bbCalc(data))));

    // Merge ALL trade markers into data (playback filtering happens at draw time)
    if (trades && trades.length > 0) {
      for (const trade of trades) {
        if (trade.barIndex >= 0 && trade.barIndex < data.length) {
          const d = data[trade.barIndex];
          d.isTrade = true;
          d.tradeSide = trade.side;
          d.tradePrice = trade.price;
          d.tradeQuantity = trade.quantity;
        }
      }
    }

    return data;
  }, [bars, trades, ema20Calc, ema50Calc, sma200Calc, bbCalc]);

  const ScaleProvider = useMemo(
    () => discontinuousTimeScaleProviderBuilder().inputDateAccessor(d => d.date),
    []
  );

  const { data, xScale, xAccessor, displayXAccessor } = useMemo(() => {
    if (!processedData.length) return { data: [], xScale: null, xAccessor: null, displayXAccessor: null };
    return ScaleProvider(processedData);
  }, [processedData, ScaleProvider]);

  const xExtents = useMemo(() => {
    if (!data.length) return [0, 0];
    const start = xAccessor(data[Math.max(0, data.length - VISIBLE_CANDLES)]);
    const end = xAccessor(data[data.length - 1]) + 2;
    return [start, end];
  }, [data, xAccessor]);

  const ol = overlays || ['ema20', 'volume'];
  const showEma20 = ol.includes('ema20');
  const showEma50 = ol.includes('ema50');
  const showSMA = ol.includes('sma200');
  const showBB = ol.includes('bb') || strategyType === 'market_making';
  const showVolume = ol.includes('volume');

  const maTooltipOptions = useMemo(() => {
    const opts = [];
    if (showEma20) opts.push({ yAccessor: ema20Calc.accessor(), type: 'EMA', stroke: '#f59e0b', windowSize: 20 });
    if (showEma50) opts.push({ yAccessor: ema50Calc.accessor(), type: 'EMA', stroke: '#8b5cf6', windowSize: 50 });
    if (showSMA) opts.push({ yAccessor: sma200Calc.accessor(), type: 'SMA', stroke: '#ec4899', windowSize: 200 });
    return opts;
  }, [ema20Calc, ema50Calc, sma200Calc, showEma20, showEma50, showSMA]);

  const h = dims.height || 450;
  const volumeH = showVolume ? VOLUME_HEIGHT : 0;
  const effectiveBottomMargin = showVolume ? chartMargin.bottom : 24;
  const priceChartHeight = h - volumeH - chartMargin.top - effectiveBottomMargin;

  // Stabilize prop references to prevent ChartCanvas from resetting on re-render
  const ratio = useMemo(() => window.devicePixelRatio || 1, []);
  const margin = useMemo(
    () => ({ ...chartMargin, bottom: effectiveBottomMargin }),
    [effectiveBottomMargin],
  );

  if (!data.length || dims.width <= 0 || priceChartHeight <= 0) {
    return (
      <div className="w-full h-full min-h-[300px] flex flex-col">
        {/* Settings gear even when no data */}
        <div className="flex justify-end px-2 py-1 flex-shrink-0">
          <div className="relative">
            <button
              type="button"
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`p-1.5 rounded transition-colors ${
                settingsOpen ? 'bg-primary/20 text-primary' : 'text-muted-foreground/50 hover:text-foreground hover:bg-white/5'
              }`}
              title="Chart Settings"
            >
              <Settings2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div
          ref={containerRef}
          className="flex-1 flex items-center justify-center"
        >
          <p className="text-muted-foreground text-sm">
            {!bars?.length ? 'Run a backtest to see the chart' : 'Loading chart...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-[300px] flex flex-col">
      {/* Chart settings gear */}
      <div className="flex justify-end px-2 py-1 flex-shrink-0">
        <div className="relative">
          <button
            type="button"
            onClick={() => setSettingsOpen(!settingsOpen)}
            className={`p-1.5 rounded transition-colors ${
              settingsOpen ? 'bg-primary/20 text-primary' : 'text-muted-foreground/50 hover:text-foreground hover:bg-white/5'
            }`}
            title="Chart Settings"
          >
            <Settings2 className="h-3.5 w-3.5" />
          </button>
          {settingsOpen && (
            <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-xl p-3 min-w-[160px]">
              <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider mb-2">Chart Behavior</p>
              {[
                { key: 'zoom', label: 'Zoom' },
                { key: 'pan', label: 'Pan' },
                { key: 'clamp', label: 'Clamp Edges' },
                { key: 'crosshair', label: 'Crosshair' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between py-1.5 cursor-pointer group">
                  <span className="text-xs text-foreground group-hover:text-primary transition-colors">{label}</span>
                  <button
                    type="button"
                    onClick={() => setChartSettings((s) => ({ ...s, [key]: !s[key] }))}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      chartSettings[key] ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      chartSettings[key] ? 'translate-x-[18px]' : 'translate-x-[3px]'
                    }`} />
                  </button>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart canvas */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 select-none"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        style={{ touchAction: 'none', WebkitUserDrag: 'none' }}
      >
        <ChartCanvas
          width={dims.width}
          height={h}
          data={data}
          xScale={xScale}
          xAccessor={xAccessor}
          displayXAccessor={displayXAccessor}
          xExtents={xExtents}
          margin={margin}
          seriesName="backtest-chart"
          ratio={ratio}
          mouseMoveEvent
          panEvent={chartSettings.pan}
          zoomEvent={chartSettings.zoom}
          clamp={chartSettings.clamp ? 'left' : false}
          zoomAnchor={mouseBasedZoomAnchor}
          zoomMultiplier={1.05}
        >
          <Chart
            id={1}
            yExtents={(d) => {
              const vals = [];
              if (isFinite(d.high)) vals.push(d.high);
              if (isFinite(d.low)) vals.push(d.low);
              if (showBB && d.bb) {
                if (isFinite(d.bb.top)) vals.push(d.bb.top);
                if (isFinite(d.bb.bottom)) vals.push(d.bb.bottom);
              }
              return vals.length ? vals : [0];
            }}
            height={priceChartHeight}
            padding={{ top: 10, bottom: 10 }}
          >
            <XAxis strokeStyle={colors.grid} tickStrokeStyle={colors.axis} tickLabelFill={colors.axis} fontSize={11} />
            <YAxis strokeStyle={colors.grid} tickStrokeStyle={colors.axis} tickLabelFill={colors.axis} tickFormat={priceFormat} fontSize={11} />

            <CandlestickSeries
              fill={(d) => (d.close >= d.open ? colors.candleUpFill : colors.candleDownFill)}
              wickStroke={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
              stroke={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
            />

            {showEma20 && <LineSeries yAccessor={d => d.ema20 ?? undefined} strokeStyle="#f59e0b" strokeWidth={1.5} />}
            {showEma50 && <LineSeries yAccessor={d => d.ema50 ?? undefined} strokeStyle="#8b5cf6" strokeWidth={1.5} />}
            {showSMA && <LineSeries yAccessor={d => d.sma200 ?? undefined} strokeStyle="#ec4899" strokeWidth={1.5} />}
            {showBB && (
              <BollingerSeries
                yAccessor={d => d.bb}
                strokeStyle={{ top: '#64748b', middle: '#64748b', bottom: '#64748b' }}
                fillStyle="rgba(100, 116, 139, 0.05)"
              />
            )}

            <MouseCoordinateX displayFormat={timeFormat('%b %d %H:%M')} />
            <MouseCoordinateY rectWidth={60} displayFormat={priceFormat} />

            <InfoTooltip />

            <EdgeIndicator
              itemType="last"
              orient="right"
              edgeAt="right"
              yAccessor={(d) => d.close}
              fill={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
              displayFormat={priceFormat}
            />

            <OHLCTooltip
              origin={(w) => [w - 220, 16]}
              textFill={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
              labelFill={colors.tooltipLabel}
            />

            {maTooltipOptions.length > 0 && (
              <MovingAverageTooltip origin={(w) => [w - 220, 32]} options={maTooltipOptions} />
            )}

            {showBB && (
              <BollingerBandTooltip
                origin={(w) => [w - 220, 32 + maTooltipOptions.length * 16]}
                yAccessor={d => d.bb}
                options={bbCalc.options()}
              />
            )}

            {(gridLevels || []).map((level) => (
              <GridLevelLine key={`gl-${level}`} yValue={level} stroke={colors.gridLevel} />
            ))}

            {/* Trade markers — filtered by playbackIndex at draw time, no data rebuild */}
            <TradeMarkersSeries maxBarIndex={playbackIndex} />
          </Chart>

          {showVolume && (
            <Chart id={2} yExtents={(d) => isFinite(d.volume) ? d.volume : 0} height={volumeH} origin={(w, h) => [0, h - volumeH]}>
              <BarSeries
                yAccessor={(d) => d.volume}
                fillStyle={(d) => d.close >= d.open ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}
              />
            </Chart>
          )}

          <CrossHairCursor strokeStyle={chartSettings.crosshair === false ? 'transparent' : colors.crosshair} />
        </ChartCanvas>
      </div>
    </div>
  );
}
